import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, ArrowUpDown, Mail as MailIcon } from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 20;

const AdminNewsletterSubscribersPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getNewsletterSubscribers(skip, LIMIT, sortDir);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching newsletter subscribers:', err);
      toast.error('Failed to load newsletter subscribers');
    } finally {
      setLoading(false);
    }
  }, [skip, sortDir]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-newsletter-subscribers-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Newsletter Subscribers</h1>
            <p className="text-muted-foreground text-sm">Everyone who signed up via the footer's "Stay Updated" form</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setSkip(0); setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); }}
            className="gap-2"
            data-testid="toggle-sort-date-btn"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            Date {sortDir === 'asc' ? '(Oldest First)' : '(Newest First)'}
          </Button>

          <span className="text-sm text-muted-foreground ml-auto">
            {total === 0 ? '0 subscribers' : `${from}-${to} of ${total}`}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-subscribers-message">
                <MailIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No newsletter subscribers yet.</p>
              </div>
            ) : (
              <Table data-testid="newsletter-subscribers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((s) => (
                    <TableRow key={s.subscriber_id} data-testid={`subscriber-row-${s.subscriber_id}`}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(s.created_at)}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate">{s.name || '—'}</TableCell>
                      <TableCell className="max-w-[260px] truncate">{s.email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {total > LIMIT && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
              data-testid="subscribers-prev-page-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + LIMIT >= total}
              onClick={() => setSkip((s) => s + LIMIT)}
              data-testid="subscribers-next-page-btn"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletterSubscribersPage;
